// src/base.ts
import { continueRender, delayRender } from "remotion";
import { NoReactInternals } from "remotion/no-react";

// src/resolve-font-subsets.ts
var isChunkSubset = (subset) => /^\[\d+\]$/.test(subset);
var compareChunkSubsets = (a, b) => {
  return Number(a.slice(1, -1)) - Number(b.slice(1, -1));
};
var resolveFontSubsetKeys = ({
  availableSubsetKeys,
  metaSubsets,
  requestedSubset
}) => {
  if (availableSubsetKeys.includes(requestedSubset)) {
    return [requestedSubset];
  }
  if (!metaSubsets.includes(requestedSubset)) {
    return [requestedSubset];
  }
  const chunkSubsets = availableSubsetKeys.filter(isChunkSubset).sort(compareChunkSubsets);
  return chunkSubsets.length === 0 ? [requestedSubset] : chunkSubsets;
};

// src/base.ts
var loadedFontsByDocument = new WeakMap;
var getLoadedFontsForDocument = (targetDocument) => {
  const cached = loadedFontsByDocument.get(targetDocument);
  if (cached) {
    return cached;
  }
  const loadedFonts = {};
  loadedFontsByDocument.set(targetDocument, loadedFonts);
  return loadedFonts;
};
var withResolvers = function() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};
var loadFontFaceOrTimeoutAfter20Seconds = (fontFace) => {
  const timeout = withResolvers();
  const int = setTimeout(() => {
    timeout.reject(new Error("Timed out loading Google Font"));
  }, 18000);
  return Promise.race([
    fontFace.load().then(() => {
      clearTimeout(int);
    }),
    timeout.promise
  ]);
};
var loadFonts = (meta, style, options) => {
  const weightsAndSubsetsAreSpecified = Array.isArray(options?.weights) && Array.isArray(options?.subsets) && options.weights.length > 0 && options.subsets.length > 0;
  if (NoReactInternals.ENABLE_V5_BREAKING_CHANGES && !weightsAndSubsetsAreSpecified) {
    throw new Error("Loading Google Fonts without specifying weights and subsets is not supported in Remotion v5. Please specify the weights and subsets you need.");
  }
  const promises = [];
  const styles = style ? [style] : Object.keys(meta.fonts);
  let fontsLoaded = 0;
  let previousAdded = Promise.resolve();
  for (const style of styles) {
    if (typeof FontFace === "undefined") {
      continue;
    }
    if (!meta.fonts[style]) {
      throw new Error(`The font ${meta.fontFamily} does not have a style ${style}`);
    }
    const targetDocument = options?.document ?? document;
    const loadedFonts = getLoadedFontsForDocument(targetDocument);
    const weights = options?.weights ?? Object.keys(meta.fonts[style]);
    for (const weight of weights) {
      if (!meta.fonts[style][weight]) {
        throw new Error(`The font ${meta.fontFamily} does not  have a weight ${weight} in style ${style}`);
      }
      const requestedSubsets = options?.subsets ?? Object.keys(meta.fonts[style][weight]);
      const availableSubsetKeys = Object.keys(meta.fonts[style][weight]);
      const subsets = [
        ...new Set(requestedSubsets.flatMap((requestedSubset) => resolveFontSubsetKeys({
          availableSubsetKeys,
          metaSubsets: meta.subsets,
          requestedSubset
        })))
      ];
      for (const subset of subsets) {
        let font = meta.fonts[style]?.[weight]?.[subset];
        if (!font) {
          throw new Error(`weight: ${weight} subset: ${subset} is not available for '${meta.fontFamily}'`);
        }
        let fontKey = `${meta.fontFamily}-${style}-${weight}-${subset}`;
        const previousPromise = loadedFonts[fontKey];
        if (previousPromise) {
          promises.push(previousPromise);
          continue;
        }
        const baseLabel = `Fetching ${meta.fontFamily} font ${JSON.stringify({
          style,
          weight,
          subset
        })}`;
        const label = weightsAndSubsetsAreSpecified ? baseLabel : `${baseLabel}. This might be caused by loading too many font variations. Read more: https://www.remotion.dev/docs/troubleshooting/font-loading-errors#render-timeout-when-loading-google-fonts`;
        const handle = delayRender(label, { timeoutInMilliseconds: 60000 });
        fontsLoaded++;
        const registerFont = (fontData) => {
          NoReactInternals.registerFontFace({
            ascentOverride: null,
            descentOverride: null,
            display: null,
            featureSettings: null,
            fontFamily: meta.fontFamily,
            fontData,
            fontUrl: font,
            format: "woff2",
            lineGapOverride: null,
            style,
            weight,
            stretch: null,
            unicodeRange: meta.unicodeRanges[subset] ?? null,
            variant: null
          });
        };
        let attempts = 2;
        const tryToLoad = () => {
          return NoReactInternals.fetchFontData(font).then((fontData) => {
            const fontFace = new FontFace(meta.fontFamily, fontData, {
              weight,
              style,
              unicodeRange: meta.unicodeRanges[subset]
            });
            return loadFontFaceOrTimeoutAfter20Seconds(fontFace).then(() => ({
              fontFace,
              fontData
            }));
          }).catch((err) => {
            if (attempts === 0) {
              loadedFonts[fontKey] = undefined;
              throw err;
            }
            attempts--;
            return tryToLoad();
          });
        };
        const promise = Promise.all([tryToLoad(), previousAdded]).then(([{ fontFace, fontData }]) => {
          targetDocument.fonts.add(fontFace);
          registerFont(fontData);
          continueRender(handle);
        });
        previousAdded = promise.catch(() => {
          return;
        });
        loadedFonts[fontKey] = promise;
        promises.push(promise);
      }
    }
    if (fontsLoaded > 20 && !options?.ignoreTooManyRequestsWarning) {
      console.warn(`Made ${fontsLoaded} network requests to load fonts for ${meta.fontFamily}. Consider loading fewer weights and subsets by passing options to loadFont(). Disable this warning by passing "ignoreTooManyRequestsWarning: true" to "options".`);
    }
  }
  return {
    fontFamily: meta.fontFamily,
    fonts: meta.fonts,
    unicodeRanges: meta.unicodeRanges,
    waitUntilDone: () => Promise.all(promises).then(() => {
      return;
    })
  };
};
var loadVariableFonts = (meta, style, options) => {
  if (!meta.variable) {
    throw new Error(`${meta.fontFamily} is not available as a variable font`);
  }
  if (options.subsets.length === 0) {
    throw new Error("Pass at least one subset to loadVariableFont()");
  }
  const fontFacesForStyle = meta.variable.fontFaces.filter((fontFace) => fontFace.style === style);
  if (fontFacesForStyle.length === 0) {
    throw new Error(`The variable font ${meta.fontFamily} does not have a style ${style}`);
  }
  const availableSubsetKeys = fontFacesForStyle.map((fontFace) => fontFace.subset);
  const subsets = [
    ...new Set(options.subsets.flatMap((requestedSubset) => resolveFontSubsetKeys({
      availableSubsetKeys,
      metaSubsets: meta.subsets,
      requestedSubset
    })))
  ];
  const promises = [];
  let previousAdded = Promise.resolve();
  for (const subset of subsets) {
    if (typeof FontFace === "undefined") {
      continue;
    }
    const font = fontFacesForStyle.find((fontFace) => fontFace.subset === subset);
    if (!font) {
      throw new Error(`subset: ${subset} is not available for the variable font '${meta.fontFamily}'`);
    }
    const fontKey = [
      meta.fontFamily,
      "variable",
      font.style,
      font.weight,
      font.stretch,
      font.subset,
      font.src
    ].join("-");
    const targetDocument = options.document ?? document;
    const loadedFonts = getLoadedFontsForDocument(targetDocument);
    const previousPromise = loadedFonts[fontKey];
    if (previousPromise) {
      promises.push(previousPromise);
      continue;
    }
    const handle = delayRender(`Fetching variable ${meta.fontFamily} font ${JSON.stringify({
      style,
      subset
    })}`, { timeoutInMilliseconds: 60000 });
    const descriptors = {
      style: font.style,
      weight: font.weight,
      unicodeRange: font.unicodeRange
    };
    if (font.stretch) {
      descriptors.stretch = font.stretch;
    }
    const registerFont = (fontData) => {
      NoReactInternals.registerFontFace({
        ascentOverride: null,
        descentOverride: null,
        display: null,
        featureSettings: null,
        fontFamily: meta.fontFamily,
        fontData,
        fontUrl: font.src,
        format: "woff2",
        lineGapOverride: null,
        style: font.style,
        weight: font.weight,
        stretch: font.stretch,
        unicodeRange: font.unicodeRange,
        variant: null
      });
    };
    let attempts = 2;
    const tryToLoad = () => {
      return NoReactInternals.fetchFontData(font.src).then((fontData) => {
        const fontFace = new FontFace(meta.fontFamily, fontData, descriptors);
        return loadFontFaceOrTimeoutAfter20Seconds(fontFace).then(() => ({
          fontFace,
          fontData
        }));
      }).catch((err) => {
        if (attempts === 0) {
          loadedFonts[fontKey] = undefined;
          throw err;
        }
        attempts--;
        return tryToLoad();
      });
    };
    const promise = Promise.all([tryToLoad(), previousAdded]).then(([{ fontFace, fontData }]) => {
      targetDocument.fonts.add(fontFace);
      registerFont(fontData);
      continueRender(handle);
    });
    previousAdded = promise.catch(() => {
      return;
    });
    loadedFonts[fontKey] = promise;
    promises.push(promise);
  }
  if (subsets.length > 20 && !options.ignoreTooManyRequestsWarning) {
    console.warn(`Made ${subsets.length} network requests to load the variable font ${meta.fontFamily}. Consider loading fewer subsets. Disable this warning by passing "ignoreTooManyRequestsWarning: true" to "options".`);
  }
  return {
    fontFamily: meta.fontFamily,
    axes: meta.variable.axes,
    waitUntilDone: () => Promise.all(promises).then(() => {
      return;
    })
  };
};

export { loadFonts, loadVariableFonts };
